/**
 * AI background replacement, shadow, and stock background search (bgremove backend).
 * Endpoints: /api/ai-background/single, /api/shadow/single, /api/background-search/search
 */
import apiClient, { createAuthConfig, isMockApi, tapCredits } from "../client";
import { appendAiImage } from "./aiUpload";

const MULTIPART = (token) => ({
  ...createAuthConfig(token),
  timeout: 300000,
  headers: {
    ...createAuthConfig(token).headers,
    "Content-Type": "multipart/form-data",
  },
});

/**
 * Append editor lineage fields to multipart AI requests.
 */
export function appendLineageToForm(form, lineage = {}) {
  if (!form || !lineage) return form;
  const entries = [
    ["parent_creation_id", lineage.parent_creation_id ?? lineage.parentCreationId],
    [
      "transparent_source_id",
      lineage.transparent_source_id ?? lineage.transparentSourceId,
    ],
    ["root_creation_id", lineage.root_creation_id ?? lineage.rootCreationId],
  ];
  entries.forEach(([key, value]) => {
    if (value != null && value !== "" && !Number.isNaN(Number(value))) {
      form.append(key, String(value));
    }
  });
  return form;
}

export async function aiBackgroundSingle(token, file, opts = {}) {
  if (isMockApi) {
    const mockSrc = opts.image_url || (file ? URL.createObjectURL(file) : "");
    return tapCredits({
      success: true,
      output_url: mockSrc || "",
      credits_used: 1,
      remaining_credits: 99,
    });
  }

  const form = new FormData();
  await appendAiImage(form, file, {
    imageUrl: opts.image_url,
    requiredMessage: "aiBackgroundSingle: provide file or opts.image_url",
  });
  if (opts.background_prompt != null) form.append("background_prompt", opts.background_prompt);
  if (opts.output_format) form.append("output_format", opts.output_format);
  if (opts.background_guidance_image) {
    form.append("background_guidance_image", opts.background_guidance_image);
  }
  if (opts.background_guidance_image_url) {
    form.append("background_guidance_image_url", opts.background_guidance_image_url);
  }
  if (opts.background_negative_prompt) form.append("background_negative_prompt", opts.background_negative_prompt);
  appendLineageToForm(form, opts.lineage);

  const response = await apiClient.post("/api/ai-background/single", form, MULTIPART(token));
  return response.data;
}

/**
 * @param {string} token
 * @param {File|null} file - Subject image when not using image_url
 * @param {object} [opts] style, intensity, direction, pose, remove_background_first, image_url
 */
export async function shadowSingle(token, file, opts = {}) {
  if (isMockApi) {
    const mockSrc = opts.image_url || (file ? URL.createObjectURL(file) : "");
    return tapCredits({
      success: true,
      shadow_url: mockSrc || "",
      credits_used: 1,
      remaining_credits: 99,
    });
  }

  const form = new FormData();
  await appendAiImage(form, file, {
    imageUrl: opts.image_url,
    requiredMessage: "shadowSingle: provide file or opts.image_url",
  });
  if (opts.style) form.append("style", opts.style);
  if (opts.intensity) form.append("intensity", opts.intensity);
  if (opts.softness != null && opts.softness !== "") {
    form.append("softness", String(Math.round(Number(opts.softness))));
  }
  if (opts.direction) form.append("direction", opts.direction);
  if (opts.direction_deg != null && opts.direction_deg !== "") {
    form.append("direction_deg", String(Math.round(Number(opts.direction_deg))));
  }
  if (opts.pose) form.append("pose", opts.pose);
  if (opts.elevation != null && opts.elevation !== "") {
    form.append("elevation", String(opts.elevation));
  }
  if (opts.remove_background_first != null) {
    form.append("remove_background_first", String(opts.remove_background_first));
  }
  if (opts.output_format) form.append("output_format", opts.output_format);
  appendLineageToForm(form, opts.lineage);

  const response = await apiClient.post("/api/shadow/single", form, MULTIPART(token));
  return response.data;
}

/**
 * @param {string} token
 * @param {{ query: string, limit?: number, page?: number, order?: string }} params
 */
export async function backgroundSearch(token, params) {
  if (isMockApi) {
    return tapCredits({
      success: true,
      result: { images: [], total: 0 },
      credits_used: 1,
      remaining_credits: 99,
    });
  }

  const sp = new URLSearchParams({
    query: params.query || "",
    limit: String(params.limit ?? 20),
    page: String(params.page ?? 1),
    order: params.order || "relevance",
  });

  const response = await apiClient.get(
    `/api/background-search/search?${sp}`,
    { ...createAuthConfig(token), timeout: 60000 }
  );
  return response.data;
}

export default {
  aiBackgroundSingle,
  shadowSingle,
  backgroundSearch,
};
